import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { Check, ExternalLink, ImageIcon, RotateCcw } from 'lucide-react'
import {
  bySection,
  discardDraft,
  fetchDraftContent,
  fetchContentBlocks,
  publishContent,
  saveContent,
  unpublishedCount,
  type ContentBlockView,
} from '../api/content'
import {
  clearFraming,
  contentPlacement,
  fetchFraming,
  isFramedBlock,
  isImageBlock,
  placementShape,
  saveFraming,
} from '../api/media'
import { useCan } from '../useSession'
import MediaPicker from './MediaPicker'
import FramingEditor from './FramingEditor'
import type { Framing } from './StudioImage'

/**
 * Where Tonya edits her own website (D-32, A-16).
 *
 * She can already do this on Wix. Replacing that with something only a
 * developer can change would be a regression, and every stale sentence would
 * become a phone call — so this is not a nice-to-have, it is the thing that
 * makes replacing Wix defensible at all.
 *
 * **Saving and publishing are separate acts, deliberately.** Saving is private
 * and reversible; publishing is the moment words reach families. Having to
 * press a second button is the pause that catches a half-written sentence.
 */
export default function ContentEditorView() {
  const queryClient = useQueryClient()
  const [error, setError] = useState<string | null>(null)

  /*
   * Which page the preview frame is showing, reported up from it (D-260).
   *
   * Held here rather than only inside `SitePreview` because it now decides
   * which fields the list below offers. The frame stays the authority on where
   * it is — links inside it navigate — so this follows the frame rather than
   * the last button pressed.
   */
  const [previewing, setPreviewing] = useState<string | undefined>(undefined)

  /*
   * Bumped after every save, publish and discard, to reload the frame (D-260).
   *
   * <b>Invalidating `['content-draft']` was never going to do this.</b> The
   * frame is a separate document with its own query cache — the comment on the
   * iframe says so — so this tab's cache and the frame's have nothing to do
   * with each other. Reported from the pilot: "clicking save draft doesn't seem
   * to actually update the preview up above unless i actively click off of that
   * tab and then back to it", and clicking off and back is exactly what
   * remounted the frame.
   */
  const [reloadToken, setReloadToken] = useState(0)

  const { data, isPending, isError } = useQuery({
    queryKey: ['content-blocks'],
    queryFn: fetchContentBlocks,
  })

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['content-blocks'] })
    // The public pages read a different endpoint; without this a publish looks
    // like it did nothing until a reload.
    queryClient.invalidateQueries({ queryKey: ['content'] })
    // And so does the preview. Checking your own work has to show your own work
    // (D-210's argument, applied to drafts).
    queryClient.invalidateQueries({ queryKey: ['content-draft'] })
    // Which reaches this tab's copy and not the frame's, so the frame is told
    // separately.
    setReloadToken((n) => n + 1)
  }

  const act = useMutation({
    mutationFn: (run: () => Promise<unknown>) => run(),
    onMutate: () => setError(null),
    onSuccess: refresh,
    onError: (e: Error) => setError(e.message),
  })

  const pending = data ? unpublishedCount(data) : 0

  return (
    /*
     * `page-shell`, not a width of its own (D-225). This screen was one of the
     * thirteen that entry consolidated and was missed; it matters now, because
     * a 52rem column leaves under 790px for the preview frame and the 1280 and
     * 1715 buttons then render identically to 768.
     */
    <div className="page-shell px-5 py-8">
      <header className="mb-5 flex flex-wrap items-end gap-4">
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-bold tracking-[0.14em] text-accent uppercase">Public site</p>
          <h1 className="font-display text-2xl">Words on your website</h1>
          <p className="mt-1.5 max-w-[38rem] text-[13.5px] text-ink-3">
            Change anything here and it saves as a draft. Nothing reaches families until
            you publish it.
          </p>
        </div>

        {/* /site, not / — signed in, "/" redirects to the calendar, so this
            used to open a second copy of the app instead of the site. */}
        <Link
          to="/site"
          target="_blank"
          className="flex shrink-0 items-center gap-1.5 rounded-lg border border-line-2 bg-surface px-3 py-2 text-[13px] font-semibold text-ink-2 no-underline hover:border-ink-3"
        >
          <ExternalLink size={13} strokeWidth={2} />
          View the site
        </Link>
      </header>

      {pending > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-3 rounded-2xl border border-warn bg-warn-bg px-4 py-3.5">
          <p className="min-w-0 flex-1 text-[13.5px]">
            <strong>
              {pending} unpublished {pending === 1 ? 'change' : 'changes'}
            </strong>{' '}
            &mdash; families are still seeing the old wording.
          </p>

          <button
            type="button"
            disabled={act.isPending}
            onClick={() =>
              act.mutate(() =>
                publishContent(data!.filter((b) => b.hasUnpublishedChanges).map((b) => b.key)),
              )
            }
            className="shrink-0 rounded-lg bg-accent px-3.5 py-2 text-[13px] font-semibold text-on-accent hover:brightness-110 disabled:opacity-50"
          >
            Publish {pending === 1 ? 'it' : 'them all'}
          </button>
        </div>
      )}

      {error && (
        <p role="alert" className="mb-4 rounded-xl border border-crit bg-crit-bg px-4 py-3 text-[13px] text-crit">
          {error}
        </p>
      )}

      {isPending && <p className="text-sm text-ink-3">Loading...</p>}
      {isError && <p role="alert" className="text-sm text-crit">Could not load the content.</p>}

      <SitePreview onPage={setPreviewing} reloadToken={reloadToken} />

      {/*
        Says the list is narrowed, and where the other pages went (D-260).

        Without it a field that was on the screen a moment ago has simply gone,
        and the control that removed it is a *preview* button — the connection
        is obvious once known and invisible before. `announcement` and `import`
        rendering as bare words is the precedent for how much a heading matters
        on this screen.
      */}
      {data && previewing && (
        <p className="mt-6 mb-1 text-[12.5px] text-ink-3">
          Showing the words on this page. Choose another page above to edit its words.
        </p>
      )}

      <div className="flex flex-col gap-6">
        {data &&
          bySection(data, previewing).map(({ area, note, blocks }) => (
            <section key={area}>
              <h2 className="text-[12px] font-bold tracking-[0.1em] text-ink-3 uppercase">
                {area}
              </h2>

              {note && <p className="mt-1 mb-2 text-[12.5px] text-ink-3">{note}</p>}
              {!note && <div className="mb-2" />}

              <div className="flex flex-col gap-2.5">
                {blocks.map((block) => (
                  <BlockEditor
                    key={block.key}
                    block={block}
                    busy={act.isPending}
                    onSave={(body) => act.mutate(() => saveContent(block.key, body))}
                    onPublish={() => act.mutate(() => publishContent([block.key]))}
                    onDiscard={() => act.mutate(() => discardDraft(block.key))}
                  />
                ))}
              </div>
            </section>
          ))}
      </div>
    </div>
  )
}

/**
 * Her page, as it would look if she published, beside the fields (D-243).
 *
 * <b>Why a frame rather than a panel.</b> Tailwind's breakpoints are viewport
 * media queries, so a 390px `<div>` inside a 1715px window renders the *desktop*
 * layout. A scaled panel cannot answer "how does this look on a phone", and
 * rewriting the public views onto container queries would change what visitors
 * see in order to make a preview easier, which is backwards. The four widths
 * are `audit.mjs`'s (D-229).
 *
 * <b>Why the editor fetches the drafts too.</b> It is not rendering them — the
 * frame does that. It asks so it can tell whether the frame will work before
 * showing one. After a deploy every database-backed endpoint refuses for about
 * seventy-five seconds (D-33), so one of this screen's requests failing while
 * another succeeds is ordinary; a frame that quietly served the *published* page
 * under a heading promising her changes is what D-209 forbids.
 */
function SitePreview({
  onPage,
  reloadToken,
}: {
  /** Where the frame is now, so the field list can narrow to it (D-260). */
  onPage: (path: string | undefined) => void
  /** Changes when something has been saved and the frame is stale (D-260). */
  reloadToken: number
}) {
  const pages = [
    { path: '/site', label: 'Home' },
    { path: '/classes', label: 'Classes' },
    { path: '/about', label: 'About' },
  ]

  const [page, setPage] = useState(pages[0].path)
  const [width, setWidth] = useState(1280)

  /*
   * A nonce, so that asking for the page the frame is *already* on still does
   * something.
   *
   * Because links inside the frame now navigate (that is the point), the frame
   * drifts away from `page` — and without this, `setPage('/site')` when `page`
   * is already `/site` changes no state, the key never changes, and the button
   * does nothing at all. A toolbar highlighting Home while the frame shows
   * Classes, whose Home button is inert, is D-204's control that lies.
   */
  const [nonce, setNonce] = useState(0)

  /*
   * Where the frame actually is, which is not always the last button pressed.
   *
   * <b>Polled, not listened for.</b> The obvious hook is the frame's `load`
   * event, and it is wrong: the frame runs the same React Router the app does,
   * so moving between her pages is a `pushState` and the document never loads
   * again. `load` fires once and the highlight then lies for the rest of the
   * session — which is the control-that-lies this change exists to remove
   * (D-204).
   *
   * Same-origin, so reading the frame's own location is allowed. A path outside
   * the three highlights nothing, which is the honest answer.
   */
  const frameRef = useRef<HTMLIFrameElement>(null)
  const [shown, setShown] = useState(pages[0].path)

  const show = (path: string) => {
    setPage(path)
    setNonce((n) => n + 1)

    /*
     * Optimistic, and the poll below corrects it if the frame lands elsewhere.
     *
     * Pressing a button *is* the intention, and waiting for the frame to load
     * before believing it costs up to a poll interval — during which the
     * highlight sits on the old page and, now that the field list follows this,
     * so does the list (D-260). Tapping About and watching the homepage fields
     * stay put for half a second reads as a button that did not work.
     */
    setShown(path)
  }

  useEffect(() => {
    const read = () => {
      try {
        const here = frameRef.current?.contentWindow?.location

        // Between a remount and its load the frame is `about:blank`, whose
        // pathname is the word "blank". Reading it would blank the highlight
        // for a moment and — now that the field list follows this — briefly
        // show every field on the site (D-260).
        if (!here || here.href === 'about:blank') return

        const path = here.pathname
        if (path) setShown((current) => (current === path ? current : path))
      } catch {
        // A cross-origin document cannot be read. Nothing in the frame should
        // ever be one, and guessing is worse than leaving the highlight alone.
      }
    }

    const timer = setInterval(read, 400)
    return () => clearInterval(timer)
  }, [])

  const { isError, isPending, refetch } = useQuery({
    queryKey: ['content-draft'],
    queryFn: fetchDraftContent,
  })

  /*
   * Told upward, not read downward — the poll above is the only thing that
   * knows where the frame actually is.
   *
   * <b>Nothing is reported while the preview is broken (D-260).</b> There is no
   * frame then, so "the words on this page" is a claim about a page nothing is
   * showing — and narrowing sixty-seven fields to twenty on the strength of a
   * fiction would take two thirds of her words away because a *picture* failed
   * to load. After a deploy every database-backed endpoint refuses for about
   * seventy-five seconds (D-33), so this is an ordinary state and not a rare
   * one. Unfiltered is the honest answer, and it is also the safe one.
   */
  useEffect(() => onPage(isError ? undefined : shown), [shown, isError, onPage])

  /*
   * Reload the frame in place when something has been saved (D-260).
   *
   * <b>In place, rather than by bumping the key.</b> Remounting rebuilds the
   * frame from `page`, which is the last *button* pressed — so a save made
   * while reading a page she had reached by following a link inside the frame
   * would silently throw her back. `location.reload()` keeps the frame where
   * it is, which is the only behaviour that is right in both cases.
   *
   * Skipped on the first render: the frame has just loaded, and reloading it
   * costs a second request for the same bytes. Falls back to a remount if the
   * document ever turns out not to be readable — nothing in the frame should be
   * cross-origin, and a stale preview is the bug being fixed.
   */
  const loaded = useRef(false)

  useEffect(() => {
    if (!loaded.current) {
      loaded.current = true
      return
    }

    try {
      frameRef.current?.contentWindow?.location.reload()
    } catch {
      setNonce((n) => n + 1)
    }
  }, [reloadToken])

  return (
    // Labelled rather than headed: `<h2>` in this screen is the field list's
    // section structure, and a toolbar that joins it is one more thing to scroll
    // past looking for a field.
    <section aria-label="Your page, with your changes" className="mt-6">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <p className="mr-auto text-[12px] font-bold tracking-[0.1em] text-ink-3 uppercase">
          Your page, with your changes
        </p>

        {pages.map((p) => (
          <button
            key={p.path}
            type="button"
            onClick={() => show(p.path)}
            aria-current={shown === p.path ? 'page' : undefined}
            className={`rounded-lg px-2.5 py-1 text-[12.5px] font-semibold ${
              shown === p.path ? 'bg-accent text-on-accent' : 'text-ink-2 hover:text-ink'
            }`}
          >
            {p.label}
          </button>
        ))}

        <span aria-hidden="true" className="mx-1 h-4 w-px bg-line-2" />

        {/*
          **Four bare numbers said nothing** (D-396).
          `asked`: *"add a title or header that says what the resolutions
          actually are… not clear."* Beside a row of page names, `390 768 1280
          1715` reads as more pages until you press one — the pills are styled
          identically, so nothing distinguishes *which page* from *how wide*.

          A word rather than a heading, because the row is one line and a
          heading over four buttons would be taller than the thing it labels.
          The `aria-hidden` is deliberate: each button already names its own
          width to a screen reader through `aria-label`, so reading the word
          again before every one of them is noise.
        */}
        <span aria-hidden="true" className="text-[11.5px] font-semibold text-ink-3">
          Screen width
        </span>

        {[390, 768, 1280, 1715].map((w) => (
          <button
            key={w}
            type="button"
            onClick={() => setWidth(w)}
            aria-label={`Preview at ${w} pixels wide`}
            aria-pressed={width === w}
            className={`rounded-lg px-2.5 py-1 text-[12.5px] font-semibold ${
              width === w ? 'bg-accent text-on-accent' : 'text-ink-2 hover:text-ink'
            }`}
          >
            {w}
          </button>
        ))}
      </div>

      {isError ? (
        // Names which request is missing. A screen joining three of them has to
        // be able to say, or a missing lookup reads as a statement about the
        // data (D-209).
        <div className="rounded-2xl border border-warn bg-warn-bg px-4 py-3.5">
          <p className="text-[13.5px]">
            Could not load the preview of your changes. Your words are safe &mdash; this is
            the picture of the page, not the page itself.
          </p>

          <button
            type="button"
            onClick={() => refetch()}
            className="mt-2 rounded-lg border border-line-2 px-3 py-1.5 text-[12.5px] font-semibold text-ink-2 hover:border-ink-3"
          >
            Try again
          </button>
        </div>
      ) : isPending ? (
        <p className="text-sm text-ink-3">Loading the preview...</p>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-line bg-surface-2 p-3">
          {/*
            `preview=1` is read inside the frame by `fetchContent`, which swaps
            the published route for the draft one. The frame is a separate
            document with its own query cache, so her drafts cannot reach the
            `['content']` key the public views read in this tab.
          */}
          <iframe
            key={`${page}-${nonce}`}
            ref={frameRef}
            title="Your page, with your changes"
            src={`${page}?preview=1`}
            style={{ width: `${width}px` }}
            /*
              No `max-w-full`: it beat the inline width, so asking for 1280
              rendered 766 and three of the four buttons gave the same answer —
              a control that lies (D-204), in the change that retires a field for
              being one. The scroller above is what handles a frame wider than
              the screen; clamping the child made that scroller unable to scroll.
            */
            className="mx-auto h-[38rem] shrink-0 rounded-xl border border-line bg-ground"
          />
        </div>
      )}
    </section>
  )
}

/**
 * Positioning one block's photograph, for that block only (D-253).
 *
 * <b>Its own component so the fetch is tied to the panel being open.</b> Ten
 * image blocks on one screen must not each ask the server how their photograph
 * is framed before anybody has asked to position one.
 *
 * <b>Scoped to the placement, never to the photograph.</b> The same portrait is
 * legitimately used in two places wanting different crops, so this writes
 * `content:<key>` and leaves the asset's own framing — and every other
 * placement — untouched (D-199, D-210).
 */
function BlockFraming({
  blockKey,
  assetId,
  src,
}: {
  blockKey: string
  assetId: string
  src: string
}) {
  const placement = contentPlacement(blockKey)

  const { data, isPending, isError } = useQuery({
    queryKey: ['framing', assetId],
    queryFn: () => fetchFraming(assetId),
  })

  /*
   * `'untouched'` is a third state, not a tidier `null`. Null is a real value
   * here — "this placement has been reset back to the photograph's own
   * framing" — and collapsing the two makes Reset impossible to hide again
   * once pressed.
   */
  const [local, setLocal] = useState<Framing | null | 'untouched'>('untouched')

  if (isPending) return <p className="text-[12.5px] text-ink-3">Loading how this sits&hellip;</p>

  if (isError || !data) {
    return (
      <p role="alert" className="text-[12.5px] text-crit">
        Could not load how this photograph is positioned.
      </p>
    )
  }

  const saved = data.overrides.find((o) => o.placement === placement)?.framing ?? null
  const override = local === 'untouched' ? saved : local
  const value = override ?? data.default

  return (
    <FramingEditor
      src={src}
      alt=""
      value={value}
      scope="just where it appears here"
      shape={placementShape(blockKey)}
      liveImmediately
      onChange={(next) => {
        setLocal(next)
        void saveFraming(assetId, next, placement)
      }}
      /*
       * Reset drops the override rather than centring it — back to the
       * photograph's own framing, which is a different act and is what the
       * DELETE means. Offered only when there is an override to drop.
       */
      onReset={
        override === null
          ? undefined
          : () => {
              setLocal(null)
              void clearFraming(assetId, placement)
            }
      }
    />
  )
}

function BlockEditor({
  block,
  busy,
  onSave,
  onPublish,
  onDiscard,
}: {
  block: ContentBlockView
  busy: boolean
  onSave: (body: string) => void
  onPublish: () => void
  onDiscard: () => void
}) {
  const [value, setValue] = useState(block.body)
  const [picking, setPicking] = useState(false)
  const [positioning, setPositioning] = useState(false)
  const canManageMedia = useCan('ManageMedia')

  // Re-syncs after a publish or discard, which change the block underneath.
  useEffect(() => setValue(block.body), [block.body])

  const dirty = value !== block.body
  const image = isImageBlock(block.key)

  /*
   * Long fields get a textarea, short ones an input. Judged by what is in the
   * field rather than by a flag on the block: a phone number in a four-line
   * box looks like a mistake, and a paragraph in a single line is unusable.
   *
   * Judged on `value`, not `block.body` — the *saved* body meant a field seeded
   * empty stayed a single-line `<input>` for ever, however much was typed into
   * it. `about.owner1.bio` is seeded empty and is where a biography goes
   * (D-245).
   */
  const long = value.length > 90 || value.includes('\n')

  return (
    <div className="rounded-2xl border border-line bg-surface px-4 py-3.5">
      <div className="mb-2 flex flex-wrap items-baseline gap-2">
        {/* The label, never the key. She has no reason to know what
            `home.hero.headline` means. */}
        <label className="text-[13.5px] font-semibold" htmlFor={block.key}>
          {block.label ?? block.key}
        </label>

        {block.hasUnpublishedChanges && (
          <span className="rounded-md bg-warn-bg px-1.5 py-0.5 text-[11px] font-semibold text-warn">
            Not published
          </span>
        )}
      </div>

      {image ? (
        /*
         * A picture, not its address. `/api/media/019fed...` is not something
         * anybody can check by reading, and one wrong character is a broken
         * image on the homepage that looks identical to a correct one here.
         */
        <div className="flex flex-wrap items-center gap-3">
          {value ? (
            <img
              src={value}
              alt=""
              className="size-20 shrink-0 rounded-xl border border-line object-cover"
            />
          ) : (
            <span className="grid size-20 shrink-0 place-items-center rounded-xl border border-dashed border-line-2 text-ink-3">
              <ImageIcon size={20} strokeWidth={1.75} />
            </span>
          )}

          {/*
            No picker for an account that cannot open one. The library and the
            framing endpoints are gated on ManageMedia while this screen is gated
            on EditSiteContent, so "Change photo" opened a dialog that 403s.
            Answered on the screen rather than by merging the two capabilities,
            which keeps "may edit words, may not replace photographs" available
            as a studio role (D-246). She still sees which photograph is on the page —
            read-only, not blank.
          */}
          {!canManageMedia && (
            <p className="text-[12.5px] text-ink-3">
              Ask whoever manages the photo library to change this one.
            </p>
          )}

          {canManageMedia && (
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setPicking(true)}
              className="rounded-lg border border-line-2 px-3 py-1.5 text-[12.5px] font-semibold text-ink-2 hover:border-ink-3"
            >
              {value ? 'Change photo' : 'Choose a photo'}
            </button>

            {/*
              Only once a photograph is chosen and saved. `mediaAssetId` is the
              block's *saved* draft id, so a picture picked a moment ago and not
              yet saved has none — and positioning it would write against the
              photograph the field no longer shows. Never offered for the logo,
              which renders `object-contain` and cannot be cropped (D-253).
            */}
            {value && isFramedBlock(block.key) && block.mediaAssetId && (
              <button
                type="button"
                onClick={() => setPositioning((p) => !p)}
                className="rounded-lg border border-line-2 px-3 py-1.5 text-[12.5px] font-semibold text-ink-2 hover:border-ink-3"
              >
                {positioning ? 'Done positioning' : 'Position photo'}
              </button>
            )}

            {value && (
              // Empty is a real answer: every image on the public site is
              // hidden while its block is blank, which is how a section is
              // taken down without anyone editing a component.
              <button
                type="button"
                onClick={() => setValue('')}
                className="text-[12.5px] font-semibold text-ink-3 hover:text-crit"
              >
                Remove
              </button>
            )}
          </div>
          )}

          {positioning && canManageMedia && value && block.mediaAssetId && (
            <div className="w-full rounded-xl border border-line bg-surface-2 p-3">
              <BlockFraming blockKey={block.key} assetId={block.mediaAssetId} src={value} />
            </div>
          )}

          {picking && (
            <MediaPicker
              current={value}
              onChoose={(url) => {
                setValue(url)
                setPicking(false)
              }}
              onClose={() => setPicking(false)}
            />
          )}
        </div>
      ) : long ? (
        <textarea
          id={block.key}
          rows={Math.min(8, Math.max(3, Math.ceil(value.length / 80)))}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className="w-full rounded-lg border border-line-2 bg-surface-2 px-3 py-2 text-[13.5px] leading-relaxed outline-none focus:border-accent focus:bg-surface"
        />
      ) : (
        <input
          id={block.key}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className="w-full rounded-lg border border-line-2 bg-surface-2 px-3 py-2 text-[13.5px] outline-none focus:border-accent focus:bg-surface"
        />
      )}

      {block.hasUnpublishedChanges && block.publishedBody !== null && (
        // What families are still seeing. Without it, "not published" is a
        // state nobody can act on — the useful question is what it differs from.
        <details className="mt-2">
          <summary className="cursor-pointer text-[12px] font-semibold text-ink-3 hover:text-ink">
            What families see now
          </summary>
          {image && block.publishedBody ? (
            <img
              src={block.publishedBody}
              alt=""
              className="mt-1.5 size-20 rounded-xl border border-line object-cover"
            />
          ) : (
            <p className="mt-1.5 rounded-lg bg-surface-2 px-3 py-2 text-[12.5px] whitespace-pre-wrap text-ink-3">
              {block.publishedBody || <em>Nothing &mdash; this is hidden while empty.</em>}
            </p>
          )}
        </details>
      )}

      <div className="mt-2.5 flex flex-wrap items-center gap-2">
        <button
          type="button"
          disabled={!dirty || busy}
          onClick={() => onSave(value)}
          className="rounded-lg border border-line-2 px-3 py-1.5 text-[12.5px] font-semibold text-ink-2 hover:border-ink-3 disabled:opacity-40"
        >
          {dirty ? 'Save draft' : 'Saved'}
        </button>

        {block.hasUnpublishedChanges && (
          <>
            <button
              type="button"
              disabled={busy || dirty}
              title={dirty ? 'Save your change first' : undefined}
              onClick={onPublish}
              className="flex items-center gap-1.5 rounded-lg bg-accent px-3 py-1.5 text-[12.5px] font-semibold text-on-accent hover:brightness-110 disabled:opacity-40"
            >
              <Check size={12} strokeWidth={3} />
              Publish
            </button>

            <button
              type="button"
              disabled={busy}
              onClick={onDiscard}
              className="flex items-center gap-1.5 text-[12.5px] font-semibold text-ink-3 hover:text-crit disabled:opacity-40"
            >
              <RotateCcw size={12} strokeWidth={2.5} />
              Undo my changes
            </button>
          </>
        )}
      </div>
    </div>
  )
}
